import { z } from 'zod';
import { zfd } from 'zod-form-data';
import {
    type InvoicePaymentFailProposalStripeAttemptMutation,
    type InvoicePaymentFailProposalStripeAttemptMutationVariables,
} from 'generated/graphql';
import { InvoicePaymentFailProposalStripeAttemptDocument } from 'generated/documents/InvoicePaymentFailProposalStripeAttemptDocument';
import type { ServerErrorResponse } from '@modules/root/graphql/responses';
import { SuccessResponse } from '@modules/root/graphql/responses';
import { UserErrorResponse } from '@modules/root/graphql/responses';
import type { ActionFunctionArgs } from '~/types/route-args';

export const INVOICE_PAYMENT_FAIL_PROPOSAL_STRIPE_ATTEMPT_API_ROUTE =
    '/remix-api/invoice-payment-fail-proposal-stripe-attempt';

const validator = zfd.formData({
    invoicePaymentId: zfd.text(
        z.string().min(1, 'Invoice Payment ID is required'),
    ),
    stripePaymentIntentId: zfd.text(z.string().optional()),
    failureReason: zfd.text(z.string().optional()),
});

export type InvoicePaymentFailProposalStripeAttemptAction =
    | SuccessResponse<{ id: number }>
    | UserErrorResponse
    | ServerErrorResponse;

/**
 * Sync-fails the active proposal Stripe attempt after Stripe.js reports a card decline.
 */
export const action = async ({ request, context }: ActionFunctionArgs) => {
    const formData = await request.formData();
    const parsed = zfd.formData(validator).safeParse(formData);
    if (!parsed.success) {
        throw new UserErrorResponse(parsed.error);
    }

    const { invoicePaymentId, stripePaymentIntentId, failureReason } =
        parsed.data;
    const result = await context.client.mutate<
        InvoicePaymentFailProposalStripeAttemptMutation,
        InvoicePaymentFailProposalStripeAttemptMutationVariables
    >({
        mutation: InvoicePaymentFailProposalStripeAttemptDocument,
        variables: {
            InvoicePaymentId: parseInt(invoicePaymentId, 10),
            StripePaymentIntentId: stripePaymentIntentId || null,
            FailureReason: failureReason || null,
        },
    });

    const invoicePayment = result.data?.invoicePaymentFailProposalStripeAttempt;

    if (invoicePayment?.__typename === 'InvoicePayment') {
        return new SuccessResponse({
            id: invoicePayment.ID,
        });
    }

    return new UserErrorResponse(
        invoicePayment?.Message ||
            'Failed to sync-fail proposal Stripe attempt',
    );
};
